import type {AppError, Result} from '@packages/utils';
import {type UseMutationResult, useMutation} from '@tanstack/react-query';
import {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import type {
  PostInquiryForeignUnivSchema,
  PostInquiryGeneralSchema,
  PostInquiryRegisterSchema,
  PostInquiryTarekomiSchema,
} from '../entities/inquiry/schema';

export const PostInquiryNonDisplayResponse = z.string();
export type PostInquiryNonDisplayResponse = z.infer<
  typeof PostInquiryNonDisplayResponse
>;
type ReturnPostValueType = Result<PostInquiryNonDisplayResponse, AppError>;

export type PostInquiryGeneralDataType = z.infer<
  typeof PostInquiryGeneralSchema
>;

type PostInquiryGeneralSettingsArgs = {
  data: PostInquiryGeneralDataType;
  version?: string;
};

const postInquiryGeneral = async ({
  data,
  version = DEFAULT_API_VERSION,
}: PostInquiryGeneralSettingsArgs): Promise<ReturnPostValueType> => {
  const path = `/${version}/service/inquiries/general`;

  const result = await defaultClient.post(
    path,
    PostInquiryNonDisplayResponse,
    data,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

type PostInquiryType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: AppError) => void;
};

export const usePostInquiryGeneral = ({
  onSuccessFnc,
  onErrorFnc,
}: PostInquiryType): UseMutationResult<
  ReturnPostValueType,
  AppError,
  PostInquiryGeneralDataType
> => {
  return useMutation(
    async (data) => {
      return postInquiryGeneral({data});
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

// POST /${version}/service/inquiries/register
export type PostInquiryRegisterDataType = z.infer<
  typeof PostInquiryRegisterSchema
>;
type PostInquiryRegisterSettingsArgs = {
  data: PostInquiryRegisterDataType;
  version?: string;
};

const postInquiry = async ({
  data,
  version = DEFAULT_API_VERSION,
}: PostInquiryRegisterSettingsArgs): Promise<ReturnPostValueType> => {
  const path = `/${version}/service/inquiries/register`;

  const result = await defaultClient.post(
    path,
    PostInquiryNonDisplayResponse,
    data,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const usePostInquiryRegister = ({
  onSuccessFnc,
  onErrorFnc,
}: PostInquiryType): UseMutationResult<
  ReturnPostValueType,
  AppError,
  PostInquiryRegisterDataType
> => {
  return useMutation(
    async (data) => {
      return postInquiry({data});
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

// POST /${version}/service/inquiries/tarekomi
export type PostInquiryTarekomiDataType = z.infer<
  typeof PostInquiryTarekomiSchema
>;

type PostInquirySettingsArgs = {
  data: PostInquiryTarekomiDataType;
  version?: string;
};

const postInquiryTarekomi = async ({
  data,
  version = DEFAULT_API_VERSION,
}: PostInquirySettingsArgs): Promise<ReturnPostValueType> => {
  const path = `/${version}/service/inquiries/tarekomi`;

  const result = await defaultClient.post(
    path,
    PostInquiryNonDisplayResponse,
    data,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const usePostInquiryTarekomi = ({
  onSuccessFnc,
  onErrorFnc,
}: PostInquiryType): UseMutationResult<
  ReturnPostValueType,
  AppError,
  PostInquiryTarekomiDataType
> => {
  return useMutation({
    async mutationFn(data) {
      return postInquiryTarekomi({data});
    },
    onSuccess() {
      onSuccessFnc();
    },
    onError(error: AppError) {
      onErrorFnc(error);
    },
  });
};

type PostInquiryForeignUnivDataType = z.infer<
  typeof PostInquiryForeignUnivSchema
>;

type PostInquiryForeignUnivSettingsArgs = {
  data: PostInquiryForeignUnivDataType;
  version?: string;
};

const postInquiryForeignUniv = async ({
  data,
  version = DEFAULT_API_VERSION,
}: PostInquiryForeignUnivSettingsArgs): Promise<ReturnPostValueType> => {
  const path = `/${version}/service/inquiries/foreign-univ`;

  const result = await defaultClient.post(
    path,
    PostInquiryNonDisplayResponse,
    data,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const usePostInquiryForeignUniv = ({
  onSuccessFnc,
  onErrorFnc,
}: PostInquiryType): UseMutationResult<
  ReturnPostValueType,
  AppError,
  PostInquiryForeignUnivDataType
> => {
  return useMutation({
    async mutationFn(data) {
      return postInquiryForeignUniv({data});
    },
    onSuccess() {
      onSuccessFnc();
    },
    onError(error: AppError) {
      onErrorFnc(error);
    },
  });
};
